import React, {
  useState,
  useContext,
  useLayoutEffect,
  useRef,
  LegacyRef,
  useCallback,
} from 'react';
import cx from 'classnames';
import useMeasure from 'use-measure';
import { GridTileType } from './InfiniteMediaLibrary';
import InfiniteMediaLibraryItem, {
  MediaLibraryAsset,
} from './InfiniteMediaLibraryItem';
import { Checkbox } from '@vimily/bonjoro-ui-components-v2/Checkbox';
import InfinityMediaItemConfigContext from '~/App/Modules/MyLibrary/Context/infinite-media-item-config-context';

export interface InfiniteMediaLibraryItemContainerProps {
  gridTileType?: GridTileType;
  asset: MediaLibraryAsset;
  onTagUpdated?: (resourceId?: string) => void;
  selected?: boolean;
  selectDisabled?: boolean;
  addSelectedId?: (id: string) => void;
  removeSelectedId?: (id: string) => void;
}

export interface CheckboxContainerProps {
  selected: boolean;
  setSelected: (selected: boolean) => void;
  disabled?: boolean;
}

export interface SelectHighlightProps {
  height: number;
  selected: boolean;
  setSelected: (selected: boolean) => void;
  disabled?: boolean;
}

function CheckboxContainer({
  selected,
  setSelected,
  disabled,
}: CheckboxContainerProps) {
  return (
    <div className='relative'>
      <div className='absolute top-2 left-2 z-10'>
        <Checkbox
          bgColorClassName='v2-bg-white'
          checked={selected}
          onChange={() => {
            setSelected(selected);
          }}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function SelectHighlight({
  height,
  selected,
  setSelected,
  disabled = false,
}: SelectHighlightProps) {
  return (
    <div
      className='relative'
      onClick={disabled ? undefined : () => setSelected(selected)}
    >
      <div
        className={cx(
          'absolute top-0 left-0 w-full opacity-50 z-10 rounded-[5px]',
          {
            'v2-bg-black': selected,
            'v2-bg-transparent': !selected,
            'cursor-not-allowed': disabled,
          }
        )}
        style={{ height }}
      />

      <div className='h-full' />
    </div>
  );
}

const InfiniteMediaLibraryItemContainer = React.memo(
  ({
    asset,
    onTagUpdated,
    gridTileType,
    addSelectedId,
    selected = false,
    selectDisabled = false,
    removeSelectedId,
  }: InfiniteMediaLibraryItemContainerProps) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [selectHighlightHeight, setSelectHighlightHeight] = useState(0);
    const containerRef = useRef<HTMLElement>(null);
    const { height } = useMeasure(containerRef);
    const detailsContainerRef = useRef<HTMLDivElement>(null);
    const itemConfig = useContext(InfinityMediaItemConfigContext);
    const isDynamicGridTileType = gridTileType === GridTileType.Dynamic;

    const commonMarginElements = 10;

    useLayoutEffect(() => {
      if (!height || !imageLoaded || !detailsContainerRef.current) {
        return;
      }

      const detailsHeight = detailsContainerRef.current.clientHeight;
      const thumbnailHeight = height - detailsHeight - commonMarginElements;
      setSelectHighlightHeight(thumbnailHeight);
    }, [height, imageLoaded]);

    const onThumbnailLoad = () => {
      setImageLoaded(true);
    };

    const { selectMode } = itemConfig;

    if (!addSelectedId || !removeSelectedId) {
      if (isDynamicGridTileType) {
        return (
          <div
            ref={containerRef as LegacyRef<HTMLDivElement>}
            className={cx('w-full transition px-2.5', {
              'opacity-0': !imageLoaded,
              'opacity-100': imageLoaded,
            })}
          >
            <InfiniteMediaLibraryItem
              ref={detailsContainerRef}
              asset={asset}
              onTagUpdated={onTagUpdated}
              gridTileType={gridTileType}
              onThumbnailLoad={onThumbnailLoad}
            />
          </div>
        );
      }

      return (
        <div className='w-full sm:max-w-[50%] lg:max-w-[33.333%] lg:px-2 lg:max-w-[25%] pb-14 pt-0 pl-0'>
          <div
            className='infinite-media-library-item-container max-w-[240px] m-auto '
            ref={containerRef as LegacyRef<HTMLDivElement>}
          >
            <InfiniteMediaLibraryItem
              ref={detailsContainerRef}
              asset={asset}
              onTagUpdated={onTagUpdated}
              gridTileType={gridTileType}
              onThumbnailLoad={onThumbnailLoad}
            />
          </div>
        </div>
      );
    }

    const isSelected = selected;

    const setSelected = useCallback(
      (selected: boolean) => {
        if (selectDisabled) {
          return;
        }

        if (!selected) {
          addSelectedId(asset.uuid);
          return;
        }

        removeSelectedId(asset.uuid);
      },
      [asset.uuid, selectDisabled, addSelectedId, removeSelectedId]
    );

    if (isDynamicGridTileType) {
      return (
        <div
          ref={containerRef as LegacyRef<HTMLDivElement>}
          className={cx('w-full transition px-2.5', {
            'opacity-0': !imageLoaded,
            'opacity-100': imageLoaded,
          })}
        >
          {selectMode && (
            <>
              <SelectHighlight
                height={selectHighlightHeight}
                selected={isSelected}
                setSelected={setSelected}
                disabled={selectDisabled}
              />
              <CheckboxContainer
                selected={isSelected}
                setSelected={setSelected}
                disabled={selectDisabled}
              />
            </>
          )}
          <InfiniteMediaLibraryItem
            ref={detailsContainerRef}
            asset={asset}
            onTagUpdated={onTagUpdated}
            gridTileType={gridTileType}
            onThumbnailLoad={onThumbnailLoad}
          />
        </div>
      );
    }

    return (
      <div className='w-full sm:max-w-[50%] lg:max-w-[33.333%] lg:px-2 lg:max-w-[25%] pb-14 pt-0 pl-0'>
        <div
          className='max-w-[240px] m-auto '
          ref={containerRef as LegacyRef<HTMLDivElement>}
        >
          {selectMode && (
            <>
              <SelectHighlight
                height={selectHighlightHeight}
                selected={isSelected}
                setSelected={setSelected}
              />
              <CheckboxContainer
                selected={isSelected}
                setSelected={setSelected}
              />
            </>
          )}
          <InfiniteMediaLibraryItem
            ref={detailsContainerRef}
            asset={asset}
            onTagUpdated={onTagUpdated}
            gridTileType={gridTileType}
            onThumbnailLoad={onThumbnailLoad}
          />
        </div>
      </div>
    );
  }
);

InfiniteMediaLibraryItemContainer.displayName =
  'InfiniteMediaLibraryItemContainer';

export default InfiniteMediaLibraryItemContainer;
